// @flow
import type {ComponentType} from 'react';
import type {Node} from 'react';
import type {Config} from 'bfu-auth-client/src/login/data/Config.js';
import type {ConfigRoutes} from 'bfu-auth-client/src/login/data/Config.js';
import type {Links} from 'bfu-auth-client/src/login/types/Types.js';
import type {Status} from 'bfu-auth-client/src/login/types/Types.js';
import type {ViewProps} from 'bfu-auth-client/src/login/types/Types.js';

import React from 'react';
import {useMachine} from '@xstate/react';

import map from 'unmutable/map';
import findKey from 'unmutable/findKey';
import flatten from 'unmutable/flatten';

import Loader from 'bfu-auth-client/src/affordance/Loader.jsx';
import CreateStateMachineLink from 'bfu-auth-client/src/login/appliance/CreateStateMachineLink.jsx';

import LoginStateMachine from 'bfu-auth-client/src/login/data/LoginStateMachine.js';
import {useHistorySync} from 'bfu-auth-client/src/login/data/RouteSync.js';
import {historyPush} from 'bfu-auth-client/src/login/data/RouteSync.js';

import ChoosePasswordView from 'bfu-auth-client/src/login/view/ChoosePasswordView.jsx';
import ConfirmView from 'bfu-auth-client/src/login/view/ConfirmView.jsx';
import ForgotPasswordView from 'bfu-auth-client/src/login/view/ForgotPasswordView.jsx';
import SigninView from 'bfu-auth-client/src/login/view/SigninView.jsx';
import SignupView from 'bfu-auth-client/src/login/view/SignupView.jsx';
import SignupConfirmView from 'bfu-auth-client/src/login/view/SignupConfirmView.jsx';
import AuthFlowLandingView from 'bfu-auth-client/src/login/view/AuthFlowLandingView.jsx';

type Props = {
    config?: Config
};

const LoginRoot =  (props: Props): Node => {

    let config: Config = props.config;
    let routes: ConfigRoutes = config.routes;

    let [current, next] = useMachine(LoginStateMachine, {
        // make state machine state changes set the url
        actions: {
            setRouteToConfirm: () => historyPush(routes.confirm),
            setRouteToForgotPassword: () => historyPush(routes.forgotPassword),
            setRouteToSignup: () => historyPush(routes.signup),
            setRouteToAuth0Landing: () => historyPush(routes.auth0Landing)
        }
    });

    // go to state machine state via pathname
    let goToPathname = (pathname: string) => {

        // correct links from /signup to /sign-up
        if(pathname === '/signup') {
            historyPush('/sign-up', true);
            goToPathname('/sign-up');
            return;
        }

        // look through valid sign in routes
        // and any that dont match, turn their destination into a redirectPath
        // and load the sign in page
        const route = findKey(url => url === pathname)(routes) ||  'signin';
        // goto a valid route
        next(`GOTO_${route}`);
    };

    // make browser history interactions drive the state machine
    useHistorySync(goToPathname);

    // create links
    let links: Links = map((pathname: string, route: string): ComponentType<any> => {
        return CreateStateMachineLink({
            pathname,
            goToPathname,
            route
        });
    })(routes);

    // create state-based messages
    let status: ?Status = flatten(true)(current.meta).status;

    let childProps: ViewProps = {
        config,
        links,
        next,
        context: current.context,
        status
    };

    if(current.matches('auth0Landing')) {
        return <AuthFlowLandingView {...childProps} />;
    }
    if(current.matches('forgotPassword')) {
        return <ForgotPasswordView {...childProps} />;
    }
    if(current.matches('choosePassword')) {
        return <ChoosePasswordView {...childProps} />;
    }
    if(current.matches('confirm')) {
        return <ConfirmView {...childProps} />;
    }
    if(current.matches('signup')) {
        return <SignupView {...childProps} />;
    }
    if(current.matches('signupConfirm')) {
        return <SignupConfirmView {...childProps} />;
    }
    if(current.matches('signupConfirmResend')) {
        return <Loader modifier="sizeMega center" text={config.signupConfirmResend.loadingText} />;
    }
    return <SigninView {...childProps} />;
};


export default LoginRoot;